// Executive dashboard — client data layer.
//
// Reads are index-light and mirror the intake-logs recipe: the portfolio
// collection is one doc per day, so the default dashboard reads N day-docs (not
// 450×N granular docs). Per-client drill-down reads telephony_daily via the
// single (client_id, date_start desc) composite. Everything is read under the
// exec_dashboard security gate; all writes are server-side (Admin SDK) except
// the two config docs (exec admin).

import { collection, doc, getDoc, getDocs, limit, orderBy, query, setDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { app, firestore } from '../firebase';
import {
  DB_BUSINESS_MONTHLY,
  DB_CLIENTS,
  DB_CLIENT_CHURN_MONTHLY,
  DB_CLIENT_SHEETS,
  DB_INTAKE_OUTCOMES_PORTFOLIO,
  DB_PNL_MONTHLY,
  DB_TURNOVER_MONTHLY,
  DB_SALES_MONTHLY,
  DB_SALES_PIPELINE,
  DB_STAFFING_MONTHLY,
  DB_TELEPHONY_ALERTS_CONFIG,
  DB_TELEPHONY_CONFIG,
  DB_TELEPHONY_DAILY,
  DB_TELEPHONY_PORTFOLIO,
  DB_TELEPHONY_SYNC,
  FN_SYNC_TELEPHONY_NOW,
  PORTFOLIO_MAX_CAP,
  PORTFOLIO_WORKING_SET_CAP,
  TELEPHONY_CONFIG_DOC,
} from './constants';
import {
  BusinessMonthly,
  Client,
  ClientChurnMonthly,
  ClientSheet,
  ExecRange,
  PnlMonthly,
  TurnoverPeriod,
  SalesMonthly,
  SalesPipeline,
  IntakeOutcomePortfolio,
  PortfolioDaily,
  RangeWindow,
  StaffingMonthly,
  TelephonyAlertsConfig,
  TelephonyConfig,
  TelephonyDaily,
  TelephonySyncState,
} from './types';

const DAY_MS = 24 * 60 * 60 * 1000;

// Epoch-ms lower bound for a preset range (ytd clamps to Jan 1; thismonth to the
// 1st of the current calendar month).
export function rangeStartMs(range: ExecRange, now = Date.now()): number {
  const d = new Date(now);
  if (range === 'thismonth') return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
  if (range === 'lastmonth') return new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime();
  if (range === 'ytd') return new Date(d.getFullYear(), 0, 1).getTime();
  const days = range === '7d' ? 7 : range === '90d' ? 90 : 30;
  return now - days * DAY_MS;
}

// The comparison ("prior") window for a range. "This month" compares to the FULL
// previous calendar month; everything else to the equal-length window before it.
export function priorWindow(range: ExecRange, win: RangeWindow, now = Date.now()): RangeWindow {
  if (range === 'thismonth') {
    const d = new Date(now);
    const startThis = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    const startPrev = new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime();
    return { startMs: startPrev, endMs: startThis - 1 };
  }
  if (range === 'lastmonth') {
    // Compare last month to the calendar month before it.
    const d = new Date(now);
    const startLast = new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime();
    const startBefore = new Date(d.getFullYear(), d.getMonth() - 2, 1).getTime();
    return { startMs: startBefore, endMs: startLast - 1 };
  }
  const len = Math.max(1, win.endMs - win.startMs);
  return { startMs: win.startMs - len, endMs: win.startMs - 1 };
}

// Parse an <input type="date"> value (yyyy-mm-dd) as local midnight / end-of-day.
function dayStartMs(iso: string): number { return new Date(`${iso}T00:00:00`).getTime(); }
function dayEndMs(iso: string): number { return new Date(`${iso}T23:59:59.999`).getTime(); }

/**
 * Resolve a preset or custom selection into the [start, end] epoch-ms window the
 * queries filter on. Custom falls back to the last 30 days if a bound is missing
 * or inverted.
 */
export function resolveRangeWindow(
  range: ExecRange,
  customStart?: string,
  customEnd?: string,
  now = Date.now(),
): RangeWindow {
  if (range === 'custom') {
    const s = customStart ? dayStartMs(customStart) : now - 30 * DAY_MS;
    const e = customEnd ? dayEndMs(customEnd) : now;
    return e >= s ? { startMs: s, endMs: e } : { startMs: e, endMs: s };
  }
  if (range === 'lastmonth') {
    const d = new Date(now);
    const startMs = new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime();
    const endMs = new Date(d.getFullYear(), d.getMonth(), 1).getTime() - 1;
    return { startMs, endMs };
  }
  return { startMs: rangeStartMs(range, now), endMs: now };
}

// Cap enough day-docs to cover the window without silently truncating a custom
// range, bounded by a hard ceiling to protect the client.
function windowCap({ startMs, endMs }: RangeWindow): number {
  const days = Math.ceil((endMs - startMs) / DAY_MS) + 2;
  return Math.min(PORTFOLIO_MAX_CAP, Math.max(PORTFOLIO_WORKING_SET_CAP, days));
}

/** Portfolio day-docs within the window, newest first (the cheap default view). */
export async function loadPortfolioRange(win: RangeWindow): Promise<PortfolioDaily[]> {
  const q = query(
    collection(firestore, DB_TELEPHONY_PORTFOLIO),
    where('date_start', '>=', win.startMs),
    where('date_start', '<=', win.endMs),
    orderBy('date_start', 'desc'),
    limit(windowCap(win)),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as PortfolioDaily);
}

/** Intake-outcome portfolio day-docs within the window (conversion, MAH, insurance). */
export async function loadIntakeOutcomeRange(win: RangeWindow): Promise<IntakeOutcomePortfolio[]> {
  const q = query(
    collection(firestore, DB_INTAKE_OUTCOMES_PORTFOLIO),
    where('date_start', '>=', win.startMs),
    where('date_start', '<=', win.endMs),
    orderBy('date_start', 'desc'),
    limit(windowCap(win)),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as IntakeOutcomePortfolio);
}

/** Granular per-client daily rows within the window (drill-down / exact values). */
export async function loadClientSeries(clientId: string, win: RangeWindow): Promise<TelephonyDaily[]> {
  const q = query(
    collection(firestore, DB_TELEPHONY_DAILY),
    where('client_id', '==', clientId),
    where('date_start', '>=', win.startMs),
    where('date_start', '<=', win.endMs),
    orderBy('date_start', 'desc'),
    limit(windowCap(win)),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as TelephonyDaily);
}

/** Monthly staffing rows (portfolio-wide agent hours). Tiny — one doc per month. */
export async function loadStaffingMonthly(): Promise<StaffingMonthly[]> {
  const snap = await getDocs(collection(firestore, DB_STAFFING_MONTHLY));
  return snap.docs.map((d) => d.data() as StaffingMonthly);
}

/** Per-client intake sheet links (IDs mirrored from DynamoDB). */
export async function loadClientSheets(): Promise<ClientSheet[]> {
  const snap = await getDocs(collection(firestore, DB_CLIENT_SHEETS));
  return snap.docs.map((d) => d.data() as ClientSheet);
}

// ---------- Business silos (Phase A) ----------
// Monthly collections are tiny (≤ ~120 docs), so each loads whole, ascending.
// business/pnl are FINANCE-GATED in rules — callers must hold the finance grant
// or the read is denied (gate views on resolveFinanceRole before calling).

/** All monthly business/financial rollups, oldest → newest. Finance-gated. */
export async function loadBusinessMonthly(): Promise<BusinessMonthly[]> {
  const q = query(collection(firestore, DB_BUSINESS_MONTHLY), orderBy('month_start', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as BusinessMonthly);
}

/** All category-level monthly P&L docs, oldest → newest. Finance-gated. */
export async function loadPnlMonthly(): Promise<PnlMonthly[]> {
  const q = query(collection(firestore, DB_PNL_MONTHLY), orderBy('month_start', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as PnlMonthly);
}

/** All monthly client-churn docs, oldest → newest. */
export async function loadClientChurn(): Promise<ClientChurnMonthly[]> {
  const q = query(collection(firestore, DB_CLIENT_CHURN_MONTHLY), orderBy('month_start', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as ClientChurnMonthly);
}

/** All turnover docs (month + quarter + year periods), oldest → newest. */
export async function loadTurnover(): Promise<TurnoverPeriod[]> {
  const q = query(collection(firestore, DB_TURNOVER_MONTHLY), orderBy('sort_ms', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as TurnoverPeriod);
}

/** Monthly sales rollups from Close, oldest → newest. */
export async function loadSalesMonthly(): Promise<SalesMonthly[]> {
  const q = query(collection(firestore, DB_SALES_MONTHLY), orderBy('month_start', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as SalesMonthly);
}

/** Current open-pipeline snapshot (null until the first Close sync runs). */
export async function loadSalesPipeline(): Promise<SalesPipeline | null> {
  const snap = await getDoc(doc(firestore, DB_SALES_PIPELINE, 'current'));
  return snap.exists() ? (snap.data() as SalesPipeline) : null;
}

/** All known clients (for display names + active/excluded flags). */
export async function loadClients(): Promise<Client[]> {
  const snap = await getDocs(collection(firestore, DB_CLIENTS));
  return snap.docs.map((d) => d.data() as Client);
}

/** Per-run sync health rows. */
export async function listSyncState(): Promise<TelephonySyncState[]> {
  const snap = await getDocs(collection(firestore, DB_TELEPHONY_SYNC));
  return snap.docs.map((d) => d.data() as TelephonySyncState);
}

// ---------- config (exec admin) ----------

export async function loadTelephonyConfig(): Promise<TelephonyConfig | null> {
  const snap = await getDoc(doc(firestore, DB_TELEPHONY_CONFIG, TELEPHONY_CONFIG_DOC));
  return snap.exists() ? (snap.data() as TelephonyConfig) : null;
}

export async function saveTelephonyConfig(cfg: Partial<TelephonyConfig>): Promise<void> {
  await setDoc(doc(firestore, DB_TELEPHONY_CONFIG, TELEPHONY_CONFIG_DOC), cfg, { merge: true });
}

export async function loadAlertsConfig(): Promise<TelephonyAlertsConfig | null> {
  const snap = await getDoc(doc(firestore, DB_TELEPHONY_ALERTS_CONFIG, TELEPHONY_CONFIG_DOC));
  return snap.exists() ? (snap.data() as TelephonyAlertsConfig) : null;
}

export async function saveAlertsConfig(cfg: Partial<TelephonyAlertsConfig>): Promise<void> {
  await setDoc(doc(firestore, DB_TELEPHONY_ALERTS_CONFIG, TELEPHONY_CONFIG_DOC), cfg, { merge: true });
}

/** Trigger an out-of-band rollup (invokes the AWS Lambda server-side). */
export async function syncTelephonyNow(backfillDays?: number): Promise<{ ok: boolean; message?: string }> {
  const functions = getFunctions(app, 'us-central1');
  const callable = httpsCallable(functions, FN_SYNC_TELEPHONY_NOW);
  const res = await callable({ backfillDays });
  return res.data as { ok: boolean; message?: string };
}

/**
 * Rollup sync health (telephony_sync/rollup) — how the last Five9 pull went.
 *
 * The pipeline can fail for days while the dashboard still renders: volume comes
 * from the billing/aggregate reports, so a failed DETAIL pull leaves the call
 * counts looking normal while hourly, dispositions and admissions quietly go to
 * zero. Reading this lets the page SAY the data is degraded instead of drawing
 * confident charts over holes.
 */
export async function loadRollupHealth(): Promise<{
  lastRunAt: number | null;
  lastError: string | null;
  quotaExhausted: boolean;
} | null> {
  try {
    const snap = await getDoc(doc(firestore, 'telephony_sync', 'rollup'));
    if (!snap.exists()) return null;
    const x: any = snap.data() || {};
    const err = x.last_error ? String(x.last_error) : null;
    return {
      lastRunAt: typeof x.last_run_at === 'number' ? x.last_run_at : null,
      lastError: err,
      // Five9 caps report operations per rolling 24h; when that trips, every
      // pull fails until the window clears.
      quotaExhausted: !!err && /seconds limit|OperationsLimitExceeded|quota/i.test(err),
    };
  } catch {
    return null;
  }
}
